import { cycleStandardNames } from "./inspectionCycleValidation";
import "./CycleOverviewInsights.scss";
import React, { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { ErrorNotify } from "../../shared/Alert";
import {
  getInspectionList,
  getInspectionCycle,
  getInspectionCycleEngagements,
} from "../../helpers/backend_helper";

const formatDate = (value) => {
  if (!value) return "\u2014";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const PreviewField = ({ label, value }) => (
  <div className="inspection-preview__field">
    <span>{label}</span>
    <strong>{value || "\u2014"}</strong>
  </div>
);

const InspectionCycleOverview = ({
  inspectionCycleId,
  actions = null,
  canCreateInspection = false,
}) => {
  const navigate = useNavigate();
  const [inspectionCycle, setInspectionCycle] = useState(null);
  const [engagements, setEngagements] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshingReviews, setRefreshingReviews] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    let isCurrent = true;

    const loadPreview = async () => {
      setLoading(true);
      try {
        const [
          inspectionCycleResponse,
          engagementsResponse,
          inspectionsResponse,
        ] = await Promise.all([
          getInspectionCycle(inspectionCycleId),
          getInspectionCycleEngagements(inspectionCycleId),
          getInspectionList().catch((error) => ({
            status: false,
            message: error?.message,
          })),
        ]);

        if (!isCurrent) return;
        if (!inspectionCycleResponse?.status)
          throw new Error(
            inspectionCycleResponse?.message ||
              "Unable to load the inspection preview."
          );

        const linkedEngagements =
          engagementsResponse?.status && Array.isArray(engagementsResponse.data)
            ? engagementsResponse.data
            : [];
        setInspectionCycle(inspectionCycleResponse.data);
        setEngagements(linkedEngagements);
        setInspections(
          inspectionsResponse?.status && Array.isArray(inspectionsResponse.data)
            ? inspectionsResponse.data.filter(
                (inspection) =>
                  Number(inspection.inspectionCycleId) ===
                  Number(inspectionCycleId)
              )
            : []
        );
        if (!engagementsResponse?.status)
          ErrorNotify(
            engagementsResponse?.message || "Unable to load linked engagements."
          );
        if (!inspectionsResponse?.status)
          ErrorNotify(
            inspectionsResponse?.message || "Unable to load linked reviews."
          );
      } catch (error) {
        if (isCurrent) {
          setInspectionCycle(null);
          setEngagements([]);
          setInspections([]);
          ErrorNotify(
            error.message || "Unable to load the inspection preview."
          );
        }
      } finally {
        if (isCurrent) setLoading(false);
      }
    };

    if (inspectionCycleId) loadPreview();
    return () => {
      isCurrent = false;
    };
  }, [inspectionCycleId]);

  const refreshReviews = async () => {
    if (refreshingReviews) return;
    setRefreshingReviews(true);
    try {
      const response = await getInspectionList();
      if (!response?.status || !Array.isArray(response.data))
        throw new Error(response?.message || "Unable to refresh inspection reviews.");
      setInspections(response.data.filter((inspection) =>
        Number(inspection.inspectionCycleId) === Number(inspectionCycleId)
      ));
    } catch (error) {
      ErrorNotify(error.message || "Unable to refresh inspection reviews.");
    } finally {
      setRefreshingReviews(false);
    }
  };

  if (loading)
    return (
      <div className="inspection-preview__empty">
        Loading inspection preview...
      </div>
    );
  if (!inspectionCycle)
    return (
      <div className="inspection-preview__empty">
        The inspection cycle could not be loaded.
      </div>
    );

  const status =
    inspectionCycle.status !== 1
      ? "Archived"
      : inspectionCycle.reviewStatus ||
        inspectionCycle.projectStatus?.name ||
        inspectionCycle.statusName ||
        "\u2014";
  const standard = cycleStandardNames(inspectionCycle);
  const reviewType =
    inspectionCycle.reviewType?.name || inspectionCycle.reviewTypeName || "\u2014";
  const inspectionCount = inspections.length;

  const rawStatus = (item) =>
    item.inspectionStatus?.name ||
    item.inspectionStatusName ||
    item.statusName ||
    "Not Started";

  const isComplete = (item) =>
    Boolean(item.completedOn) ||
    /complete|closed|approved/i.test(rawStatus(item));

  const isOverdue = (item) =>
    !isComplete(item) &&
    (item.isOverdue ||
      (item.dueDate &&
        new Date(item.dueDate).getTime() < new Date().setHours(0, 0, 0, 0)));

  const isAtRisk = (item) =>
    !isComplete(item) &&
    (isOverdue(item) ||
      item.isAtRisk ||
      /risk|attention|returned/i.test(rawStatus(item)));
  const completedCount = inspections.filter(isComplete).length;
  const inProgressCount = inspections.filter(
    (item) =>
      !isComplete(item) &&
      /progress|review|assigned|sign/i.test(rawStatus(item))
  ).length;
  const riskCount = inspections.filter(isAtRisk).length;
  const overdueCount = inspections.filter(isOverdue).length;
  const checklistProgress = Number(inspectionCycle.reviewProgress ?? 0);
  const safeProgress = Number.isFinite(checklistProgress)
    ? Math.max(0, Math.min(100, Math.round(checklistProgress)))
    : 0;

  const inspectionStatus = (item) =>
    isComplete(item)
      ? "Complete"
      : isOverdue(item)
      ? "Overdue"
      : isAtRisk(item)
      ? "At Risk"
      : rawStatus(item);

  const inspectionProgress = (item) =>
    Math.max(
      0,
      Math.min(
        Number(
          item.percentage ??
            item.inspectionPercentage ??
            item.reviewPercentage ??
            item.progress ??
            0
        ),
        100
      )
    );

  const countValue = (item, field) => (item[field] == null ? "\u2014" : item[field]);

  const total = (field) =>
    inspections.length && inspections.every((item) => item[field] != null)
      ? inspections.reduce((sum, item) => sum + Number(item[field]), 0)
      : "\u2014";
  const createdBy = inspectionCycle.createdByName || "Unavailable";
  const lead = inspectionCycle.inspectionLeadName ||
    (inspectionCycle.inspectionLeadId ? "Unavailable" : "Not assigned");
  const filtered = inspections.filter((item) => {
    const engagement = engagements.find(
      (entry) => Number(entry.id) === Number(item.engagementId)
    );
    return (
      `${item.inspectionNo || ""} ${item.title || ""} ${
        engagement?.name || item.engagementName || item.engagement?.name || ""
      } ${item.ownerName || item.reviewerName || ""}`
        .toLowerCase()
        .includes(search.toLowerCase()) &&
      (!statusFilter || inspectionStatus(item) === statusFilter)
    );
  });
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageItems = filtered.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );
  const notStartedCount = Math.max(0, inspectionCount - completedCount - inProgressCount);
  const percentOf = (count) => inspectionCount ? Math.round(count / inspectionCount * 100) : 0;
  const recentReviews = inspections.filter(item => item.lastActivityOn || item.updatedOn || item.createdOn)
    .slice().sort((a, b) => new Date(b.lastActivityOn || b.updatedOn || b.createdOn) - new Date(a.lastActivityOn || a.updatedOn || a.createdOn)).slice(0, 3);
  const openReview = (event) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!document.startViewTransition || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    event.preventDefault();
    const destination = event.currentTarget.getAttribute("href");
    document.documentElement.classList.add("cycle-review-slide");
    try {
      const transition = document.startViewTransition(() => {
        flushSync(() => navigate(destination));
      });
      transition.finished.catch(() => {}).finally(() => document.documentElement.classList.remove("cycle-review-slide"));
    } catch {
      document.documentElement.classList.remove("cycle-review-slide");
      navigate(destination);
    }
  };

  const createInspection = () =>
    navigate(`/inspections/${inspectionCycleId}/edit?step=engagements`);
  const metrics = [
    [
      "Inspection Reviews",
      inspectionCount,
      "bx-file",
      `${engagements.length} engagements`,
      "",
    ],
    [
      "Complete",
      completedCount,
      "bx-check-circle",
      `${safeProgress}% of reviews`,
      "is-success",
    ],
    [
      "In Progress",
      inProgressCount,
      "bx-refresh",
      `${
        inspectionCount
          ? Math.round((inProgressCount / inspectionCount) * 100)
          : 0
      }% of reviews`,
      "",
    ],
    ["At Risk", riskCount, "bx-error", `${overdueCount} overdue`, "is-warning"],
    [
      "Open Queries",
      total("openQueryCount"),
      "bx-message-square-detail",
      "Across cycle reviews",
      "is-danger",
    ],
    [
      "Findings",
      total("findingCount"),
      "bx-list-ul",
      "Across cycle reviews",
      "is-danger",
    ],
  ];

  return (
    <section
      id="cycle-overview"
      className="inspection-preview inspection-preview--workspace inspection-preview--redesign"
      aria-label="Inspection cycle workspace"
    >
      <header className="inspection-preview__workspace-header">
        <div className="inspection-preview__identity">
          <div className="inspection-preview__title-row">
            <h1 className="piers-page-title">{inspectionCycle.title || "Untitled inspection cycle"}</h1>
            {status !== "\u2014" && <span className="inspection-preview__status">{status}</span>}
          </div>
          <p>
            {formatDate(inspectionCycle.startDate)} -{" "}
            {formatDate(inspectionCycle.endDate)}
            <b>|</b>
            {engagements.length} Engagements<b>|</b>Created by: {createdBy}
          </p>
        </div>
        {typeof actions === "function" ? actions() : actions}
      </header>
      <section
        className="cycle-overall-progress"
        aria-label="Overall cycle progress"
      >
        <h2>Overall Cycle Progress</h2>
        <div
          className="inspection-preview__bar"
          role="progressbar"
          aria-label="Checklist progress"
          aria-valuenow={safeProgress}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <i style={{ width: `${safeProgress}%` }} />
        </div>
        <strong>{safeProgress}%</strong>
        <span>
          {completedCount} of {inspectionCount} inspection reviews complete
        </span>
        <span>{inProgressCount} in progress</span>
        <span>{riskCount} at risk</span>
        <span>{overdueCount} overdue</span>
      </section>
      <section
        className="inspection-preview__metrics"
        aria-label="Inspection cycle summary"
      >
        {metrics.map(([label, value, icon, detail, tone]) => (
          <article key={label} className={`${tone} cycle-summary-link-card`}>
            <a className="cycle-summary-link" href="#cycle-reviews" aria-label={`${label}: ${value}. View inspection reviews`} />
            <i className={`bx ${icon}`} />
            <div>
              <strong>{value}</strong>
              <span>{label}</span>
              <small>{detail}</small>
            </div>
          </article>
        ))}
      </section>
      <div className="inspection-preview__dashboard">
        <section
          id="cycle-basic-details"
          className="inspection-preview__panel inspection-preview__basic-details"
        >
          <div className="inspection-preview__panel-title">
            <h2>
              <i className="bx bx-file" /> Inspection Basic Details
            </h2>
            {canCreateInspection && (
              <button
                type="button"
                className="cycle-text-action"
                onClick={() =>
                  navigate(`/inspections/${inspectionCycleId}/edit`)
                }
              >
                <i className="bx bx-pencil" /> Edit Details
              </button>
            )}
          </div>
          <div className="inspection-preview__details">
            <PreviewField
              label="Inspection Name"
              value={inspectionCycle.title}
            />
            <PreviewField label="Inspection Type" value={reviewType} />
            <PreviewField label="Status" value={status} />
            <PreviewField
              label="Start Date"
              value={formatDate(inspectionCycle.startDate)}
            />
            <PreviewField
              label="End Date"
              value={formatDate(inspectionCycle.endDate)}
            />
            <PreviewField label="Inspection Lead" value={lead} />
            <PreviewField
              label="Engagements Selected"
              value={`${engagements.length} Engagements`}
            />
            <PreviewField label="Created By" value={createdBy} />
            <PreviewField
              label="Created On"
              value={formatDate(inspectionCycle.createdOn)}
            />
          </div>
          <div className="inspection-preview__description">
            <span>Description</span>
            <p>{inspectionCycle.description || "No description provided."}</p>
          </div>
          <small className="cycle-standards">Standards: {standard}</small>
        </section>
        <aside className="cycle-insights">
          <section className="inspection-preview__panel cycle-engagement-status">
            <h2><i className="bx bx-group" aria-hidden="true" /> Engagement Status</h2>
            <div className="cycle-engagement-status__body">
              <div className="cycle-engagement-status__donut" role="img" aria-label={`${inspectionCount} engagements: ${completedCount} complete, ${inProgressCount} in progress, ${notStartedCount} not started`} style={{ background: `conic-gradient(#00aa62 0% ${percentOf(completedCount)}%, #075bff ${percentOf(completedCount)}% ${percentOf(completedCount + inProgressCount)}%, #b1bfd9 ${percentOf(completedCount + inProgressCount)}% 100%)` }}><div><b>{inspectionCount}</b><small>Engagements</small></div></div>
              <ul>{[["Complete", completedCount, "complete"], ["In Progress", inProgressCount, "progress"], ["Not Started", notStartedCount, "pending"]].map(([label, count, tone]) => <li key={label}><i className={`is-${tone}`} /><span>{label}</span><b>{count} ({percentOf(count)}%)</b></li>)}</ul>
            </div>
          </section>
          <section className="inspection-preview__panel cycle-recent-activity">
            <h2><i className="bx bx-time-five" aria-hidden="true" /> Recent Activity <a href="#cycle-reviews">View All</a></h2>
            {recentReviews.length ? <ol>{recentReviews.map(item => <li key={item.id}><i className={`cycle-activity-dot ${isComplete(item) ? "is-complete" : ""}`} /><div><Link to={`/inspectionreview/${item.id}`}>{item.inspectionNo || `INS-${item.id}`}</Link><time>{formatDate(item.lastActivityOn || item.updatedOn || item.createdOn)}</time><p>{item.lastActivityOn || item.updatedOn ? "Review updated" : "Review created"}</p></div></li>)}</ol> : <p className="cycle-insights__empty">No recent activity available.</p>}
          </section>
        </aside>
      </div>
      <section
        id="cycle-reviews"
        className="inspection-preview__panel cycle-review-panel"
        aria-label="Inspection reviews"
      >
        <header className="cycle-review-toolbar">
          <h2>Inspection Reviews ({inspectionCount})</h2>
          <input
            type="search"
            aria-label="Search reviews or engagements"
            placeholder="Search reviews or engagements..."
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
          />
          <label className="cycle-review-filters">
            Status{" "}
            <select
              aria-label="Filter reviews by status"
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                setPage(1);
              }}
            >
              <option value="">All statuses</option>
              {[...new Set(inspections.map(inspectionStatus))]
                .sort()
                .map((value) => (
                  <option key={value}>{value}</option>
                ))}
            </select>
          </label>
          <button className="piers-refresh-button"
            type="button"
            aria-label="Refresh reviews"
            onClick={refreshReviews}
            disabled={refreshingReviews}
            aria-busy={refreshingReviews}
          >
            <i className={`bx bx-refresh${refreshingReviews ? " bx-spin" : ""}`} />
          </button>
        </header>

        <div className="inspection-preview__table-wrap">
          <table className="inspection-preview__table inspection-preview__inspection-table">
            <thead>
              <tr>
                <th>Review ID</th>
                <th>Engagement</th>
                <th>Inspector</th>
                <th>Checklist Progress</th>
                <th>Queries</th>
                <th>Findings</th>
                <th>Last Activity</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((item) => {
                const engagement = engagements.find(
                  (entry) => Number(entry.id) === Number(item.engagementId)
                );
                const percent = inspectionProgress(item);
                return (
                  <tr key={item.id}>
                    <td>
                      <strong>{item.inspectionNo || `INS-${item.id}`}</strong>
                    </td>
                    <td>
                      {engagement?.name ||
                        item.engagementName ||
                        item.engagement?.name ||
                        "\u2014"}
                      <span>
                        {Array.isArray(item.reviewTypes)
                          ? item.reviewTypes.join(", ")
                          : item.inspectionType?.name || ""}
                      </span>
                    </td>
                    <td>
                      {item.ownerName ||
                        item.assignedUser?.fullName ||
                        item.reviewerName ||
                        "Unassigned"}
                    </td>
                    <td>
                      <div className="inspection-preview__bar">
                        <i style={{ width: `${percent}%` }} />
                      </div>
                      <b>{percent}%</b>
                    </td>
                    <td>{countValue(item, "openQueryCount")}</td>
                    <td>{countValue(item, "findingCount")}</td>
                    <td>{formatDate(item.lastActivityOn || item.updatedOn)}</td>
                    <td>
                      <em
                        className={
                          isComplete(item)
                            ? "is-complete"
                            : isAtRisk(item)
                            ? "is-attention"
                            : "is-progress"
                        }
                      >
                        {inspectionStatus(item)}
                      </em>
                    </td>
                    <td>
                      <Link
                        onClick={openReview}
                        to={
                          item.isDraft
                            ? `/inspections/${item.id}/setup?engagementId=${
                                item.engagementId
                              }&cycleId=${inspectionCycleId}&returnTo=${encodeURIComponent(
                                `/inspections/${inspectionCycleId}/preview`
                              )}`
                            : `/inspectionreview/${item.id}`
                        }
                      >
                        {item.isDraft ? "Resume" : "Open"}{" "}
                        <i className="bx bx-right-arrow-alt" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
              {!pageItems.length && (
                <tr>
                  <td colSpan="9">
                    <div className="inspection-preview__empty">
                      {inspectionCount
                        ? "No reviews match your search or filters."
                        : "No reviews have been created for this cycle."}
                      {!inspectionCount && canCreateInspection && (
                        <button type="button" onClick={createInspection}>
                          Add Engagements
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <footer className="cycle-review-pagination">
          <span>
            {filtered.length ? (currentPage - 1) * pageSize + 1 : 0}&ndash;
            {Math.min(currentPage * pageSize, filtered.length)} of{" "}
            {filtered.length}
          </span>
          <label>
            Records per page{" "}
            <select
              aria-label="Review records per page"
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
            >
              {[5, 10, 25, 50, 100].map((size) => (
                <option key={size}>{size}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            disabled={currentPage <= 1}
            onClick={() => setPage(currentPage - 1)}
            aria-label="Previous reviews page"
          >
            &lsaquo;
          </button>
          <span>
            Page {currentPage} of {pageCount}
          </span>
          <button
            type="button"
            disabled={currentPage >= pageCount}
            onClick={() => setPage(currentPage + 1)}
            aria-label="Next reviews page"
          >
            &rsaquo;
          </button>
        </footer>
      </section>
    </section>
  );
};

export default InspectionCycleOverview;
